import { useI18n } from 'vue-i18n';

/**
 * Utility for managing browser navigation protection when there are unsaved changes.
 * Callers pass an accessor that reports whether there are unsaved changes (e.g. a
 * store getter), so this composable stays decoupled from how changes are tracked.
 */
export function useNavigationProtection() {
    const { t } = useI18n();

    /**
     * Setup browser navigation protection
     */
    function setupNavigationProtection(hasUnsavedChanges: () => boolean) {
        const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
            // Under Cypress, skip the native unsaved-changes prompt. In headless Chrome a
            // `beforeunload` that calls preventDefault() blocks Cypress's between-test
            // teardown navigation and hangs the whole run (the next spec never starts).
            // Real users are unaffected — `Cypress` is never present in production.
            if (hasUnsavedChanges() && !("Cypress" in globalThis)) {
                const message = t('textMaintenance.unsavedChanges');
                event.preventDefault();
                return message;
            }
        };

        // Use globalThis for better compatibility
        globalThis.addEventListener('beforeunload', beforeUnloadHandler);

        // Return cleanup function
        return () => {
            globalThis.removeEventListener('beforeunload', beforeUnloadHandler);
        };
    }

    /**
     * Confirmation dialog for navigation
     */
    function confirmNavigation(hasUnsavedChanges: () => boolean): boolean {
        if (hasUnsavedChanges()) {
            return confirm(t('textMaintenance.confirmLeave'));
        }
        return true;
    }

    return {
        setupNavigationProtection,
        confirmNavigation
    };
}
